import React, { useState, useMemo, useEffect } from 'react';
import * as HeroIcons from '@heroicons/react/24/outline';
import {
  MagnifyingGlassIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { GRAMMAR_CATEGORIES, GRAMMAR_TOPICS, CEFR_LEVELS } from '../data/grammarData';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import SpeakButton from '../components/ui/SpeakButton';
import { bookmarksAPI } from '../services/api';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import { BookmarkIcon as BookmarkOutlineIcon } from '@heroicons/react/24/outline';

const LEVEL_COLORS = {
  A1: { bg: 'bg-green-100', text: 'text-green-700', active: 'bg-green-500 text-white', border: 'border-green-300' },
  A2: { bg: 'bg-teal-100', text: 'text-teal-700', active: 'bg-teal-500 text-white', border: 'border-teal-300' },
  B1: { bg: 'bg-blue-100', text: 'text-blue-700', active: 'bg-blue-500 text-white', border: 'border-blue-300' },
  B2: { bg: 'bg-brand-primary/15', text: 'text-brand-primary', active: 'bg-brand-primary text-primary-foreground', border: 'border-brand-primary/30' },
  C1: { bg: 'bg-orange-100', text: 'text-orange-700', active: 'bg-orange-500 text-white', border: 'border-orange-300' },
  C2: { bg: 'bg-red-100', text: 'text-red-700', active: 'bg-red-500 text-white', border: 'border-red-300' },
};

const LEVEL_DESCRIPTIONS = {
  A1: 'Fillestar',
  A2: 'Elementar',
  B1: 'Mesatar',
  B2: 'I pavarur',
  C1: 'I avancuar',
  C2: 'Zotërim',
};

const LevelBadge = ({ level, small }) => {
  const c = LEVEL_COLORS[level] || {};
  return (
    <span className={`inline-flex items-center font-bold rounded-full ${small ? 'text-[9px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5'} ${c.bg} ${c.text}`}>
      {level}
    </span>
  );
};

const ComprehensiveGrammarPage = () => {
  const [activeCategory, setActiveCategory] = useState('');
  const [activeLevel, setActiveLevel] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopicId, setSelectedTopicId] = useState(null);

  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [bookmarkLoading, setBookmarkLoading] = useState(false);

  // Load bookmarks on mount
  useEffect(() => {
    bookmarksAPI.list('grammar_topic')
      .then((res) => {
        const ids = new Set((res.data.bookmarks || []).map(b => b.content_id));
        setBookmarkedIds(ids);
      })
      .catch(() => {});
  }, []);

  const toggleBookmark = async (topic) => {
    if (bookmarkLoading) return;
    setBookmarkLoading(false);
    const isBookmarked = bookmarkedIds.has(topic.id);
    try {
      if (isBookmarked) {
        const res = await bookmarksAPI.list('grammar_topic');
        const match = (res.data.bookmarks || []).find(b => b.content_id === topic.id);
        if (match) {
          await bookmarksAPI.remove(match.id);
          setBookmarkedIds(prev => {
            const next = new Set(prev);
            next.delete(topic.id);
            return next;
          });
        }
      } else {
        await bookmarksAPI.add('grammar_topic', topic.id, topic.title);
        setBookmarkedIds(prev => new Set([...prev, topic.id]));
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Quiz State
  const [quizActive, setQuizActive] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [answerFeedback, setAnswerFeedback] = useState(null);

  // Filter topics
  const filteredTopics = useMemo(() => {
    return GRAMMAR_TOPICS.filter(topic => {
      if (searchTerm) return topic.title.toLowerCase().includes(searchTerm.toLowerCase());
      const catMatch = activeCategory ? topic.category === activeCategory : true;
      const lvlMatch = activeLevel ? topic.level === activeLevel : true;
      return catMatch && lvlMatch;
    });
  }, [activeCategory, activeLevel, searchTerm]);

  const selectedTopic = useMemo(() => {
    if (!selectedTopicId) return null;
    return GRAMMAR_TOPICS.find(t => t.id === selectedTopicId);
  }, [selectedTopicId]);

  const handleTopicSelect = (topicId) => {
    setSelectedTopicId(topicId);
    resetQuiz();
    const content = document.getElementById('grammar-content');
    if (content) content.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetQuiz = () => {
    setQuizActive(false);
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setShowResult(false);
    setAnswerFeedback(null);
  };

  const startQuiz = () => {
    setQuizActive(true);
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setShowResult(false);
  };

  const handleAnswerClick = (index) => {
    if (answerFeedback) return;
    const currentQuestion = selectedTopic.exercises[currentQuestionIndex];
    const isCorrect = index === currentQuestion.correct;
    setAnswerFeedback({ isCorrect, explanation: currentQuestion.explanation });
    setUserAnswers([...userAnswers, { selectedIndex: index, isCorrect }]);
  };

  const nextQuestion = () => {
    setAnswerFeedback(null);
    if (currentQuestionIndex + 1 < selectedTopic.exercises.length) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setShowResult(true);
    }
  };

  const IconComponent = (iconName, size = "w-5 h-5") => {
    const Icon = HeroIcons[iconName] || HeroIcons.QuestionMarkCircleIcon;
    return <Icon className={size} />;
  };

  const score = userAnswers.filter(a => a.isCorrect).length;

  const handleLevelClick = (lvl) => {
    setActiveLevel(prev => prev === lvl ? '' : lvl);
    setActiveCategory('');
    setSearchTerm('');
    setSelectedTopicId(null);
  };

  const handleCategoryClick = (catId) => {
    setActiveCategory(prev => prev === catId ? '' : catId);
    setActiveLevel('');
    setSearchTerm('');
    setSelectedTopicId(null);
  };

  return (
    <DashboardLayout noPadding={true}>
      <div className="flex flex-col lg:flex-row h-full overflow-hidden bg-brand-background">

        {/* Sidebar */}
        <div className="w-full lg:w-72 bg-brand-background flex flex-col h-full z-10 font-body shadow-[0_0_0_1px_rgba(15,23,42,0.04),0_18px_28px_-24px_rgba(15,23,42,0.2)]">
          <Card className="p-5 rounded-none shadow-sm border-0 bg-white/80">
            <h1
              className="text-xl font-display font-bold text-brand-text flex items-center gap-2 cursor-pointer hover:text-brand-primary transition-colors duration-200"
              onClick={() => { setSelectedTopicId(null); setActiveCategory(''); setActiveLevel(''); setSearchTerm(''); }}
            >
              <HeroIcons.BookOpenIcon className="w-6 h-6 text-brand-primary" />
              Libraria
            </h1>
            {/* Search */}
            <div className="mt-3 relative">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-text/50" />
              <Input
                type="text"
                placeholder="Kërko leksionin..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setActiveCategory(''); setActiveLevel(''); setSelectedTopicId(null); }}
              />
            </div>
            {/* CEFR Level Filters */}
            <div className="mt-3">
              <p className="text-[10px] font-bold text-brand-text/50 uppercase tracking-widest mb-2">Niveli CEFR</p>
              <div className="flex flex-wrap gap-1.5">
                {CEFR_LEVELS.map(lvl => {
                  const c = LEVEL_COLORS[lvl];
                  const isActive = activeLevel === lvl;
                  return (
                    <button
                      key={lvl}
                      onClick={() => handleLevelClick(lvl)}
                      className={`text-xs font-bold px-2.5 py-1 rounded-full border transition-all ${isActive ? `${c.active} border-transparent shadow-sm` : `${c.bg} ${c.text} ${c.border} hover:opacity-80`
                        }`}
                    >
                      {lvl}
                    </button>
                  );
                })}
                {activeLevel && (
                  <button
                    onClick={() => handleLevelClick('')}
                    className="text-xs font-bold px-2.5 py-1 rounded-full border border-brand-secondary/20 text-brand-text/50 hover:bg-brand-primary/10"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </Card>

          <div className="flex-1 overflow-y-auto p-3 space-y-4">
            {/* Category filters (only when not level-filtered) */}
            {!searchTerm && (
              <div>
                <p className="px-3 text-[10px] font-bold text-brand-text/50 uppercase tracking-widest mb-2">Kategoritë</p>
                <div className="grid grid-cols-1 gap-1">
                  {GRAMMAR_CATEGORIES.map(cat => (
                    <Button
                      key={cat.id}
                      variant={activeCategory === cat.id ? 'secondary' : 'ghost'}
                      className={cn(
                        'w-full justify-start gap-3 h-auto py-2 px-3 font-medium',
                        activeCategory !== cat.id && 'text-brand-text/70',
                      )}
                      onClick={() => handleCategoryClick(cat.id)}
                    >
                      {IconComponent(cat.icon)}
                      {cat.title}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Topics list */}
            {(activeCategory || activeLevel || searchTerm) && (
              <div className="animate-fadeIn">
                <p className="px-3 text-[10px] font-bold text-brand-text/50 uppercase tracking-widest mb-2">
                  Leksionet {filteredTopics.length > 0 && <span className="normal-case font-normal">({filteredTopics.length})</span>}
                </p>
                {filteredTopics.length === 0 ? (
                  <p className="px-3 text-sm text-brand-text/50 italic">Asnjë temë e gjetur.</p>
                ) : (
                  <div className="space-y-1">
                    {filteredTopics.map(topic => (
                      <Button
                        key={topic.id}
                        variant={selectedTopicId === topic.id ? 'secondary' : 'ghost'}
                        className={cn(
                          'group w-full h-auto p-2.5 rounded-xl text-sm justify-between text-left font-normal',
                          selectedTopicId === topic.id ? 'text-brand-primary' : 'text-brand-text/70',
                        )}
                        onClick={() => handleTopicSelect(topic.id)}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {topic.level && <LevelBadge level={topic.level} small />}
                          <span className="font-medium truncate">{topic.title}</span>
                        </div>
                        <ChevronRightIcon className={cn('w-4 h-4 flex-shrink-0 transition-transform', selectedTopicId === topic.id ? 'translate-x-1 text-brand-primary' : 'opacity-0 group-hover:opacity-100')} />
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div id="grammar-content" className="flex-1 overflow-y-auto bg-brand-background/50 scroll-smooth">
          {/* STATE 1: Initial Grid (No selection, no search) */}
          {!selectedTopicId && !activeCategory && !activeLevel && !searchTerm && (
            <div className="max-w-4xl mx-auto p-8 lg:p-16 animate-fadeIn">
              <header className="mb-12">
                <h2 className="text-3xl font-black text-brand-text mb-2">Mirësevini në Librari</h2>
                <p className="text-brand-text/60 font-medium">Zgjidhni një kategori ose nivel për të filluar mësimin e gramatikës gjermane.</p>
              </header>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {GRAMMAR_CATEGORIES.map((cat, idx) => (
                  <Card
                    key={cat.id}
                    className="group relative border-0 bg-white p-6 text-left shadow-md cursor-pointer transition-all duration-200 hover:shadow-lg animate-fadeIn"
                    style={{ animationDelay: `${idx * 0.05}s` }}
                    onClick={() => handleCategoryClick(cat.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCategoryClick(cat.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 group-hover:bg-brand-primary flex items-center justify-center mb-4 transition-colors duration-200 border-[3px] border-brand-primary/15">
                      {IconComponent(cat.icon, "w-6 h-6 text-brand-primary group-hover:text-primary-foreground")}
                    </div>
                    <h3 className="text-lg font-bold text-brand-text mb-1">{cat.title}</h3>
                    <p className="text-xs text-brand-text/50">Eksploro të gjitha leksionet rreth kësaj kategorie.</p>
                    <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRightIcon className="w-5 h-5 text-brand-primary" />
                    </div>
                  </Card>
                ))}
              </div>

              <Card className="mt-12 overflow-hidden border border-brand-primary/15 bg-gradient-to-br from-white via-white to-amber-50/70 p-6 shadow-sm md:p-8">
                <div className="mb-6 flex items-start justify-between gap-4">
                  <div>
                    <p className="mb-2 text-[10px] font-black uppercase tracking-[0.2em] text-brand-primary">Rruga juaj e mësimit</p>
                    <h3 className="text-xl font-bold text-brand-text md:text-2xl">Zgjidhni nivelin tuaj</h3>
                    <p className="mt-1 text-sm text-brand-text/60">Mësime gramatike të organizuara sipas niveleve CEFR.</p>
                  </div>
                  <span className="hidden shrink-0 rounded-full bg-brand-primary/10 px-3 py-1.5 text-xs font-bold tracking-wider text-brand-text sm:inline-flex">
                    A1 — C2
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  {CEFR_LEVELS.map(lvl => {
                    const c = LEVEL_COLORS[lvl];
                    return (
                      <button
                        key={lvl}
                        type="button"
                        onClick={() => handleLevelClick(lvl)}
                        aria-label={`Zgjidh nivelin ${lvl} - ${LEVEL_DESCRIPTIONS[lvl]}`}
                        className={`group flex min-h-[84px] items-center gap-2.5 rounded-xl border bg-white p-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 ${c.border}`}
                      >
                        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-black ${c.bg} ${c.text}`}>
                          {lvl}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-bold text-brand-text">Niveli {lvl}</span>
                          <span className="mt-0.5 block text-[10px] leading-tight text-brand-text/55">{LEVEL_DESCRIPTIONS[lvl]}</span>
                        </span>
                        <ArrowRightIcon className="hidden h-3.5 w-3.5 shrink-0 text-brand-text/35 transition-transform group-hover:translate-x-0.5 sm:block" />
                      </button>
                    );
                  })}
                </div>
              </Card>
            </div>
          )}

          {/* STATE 2: Filtered Topic List (Category or Level selected, but no specific topic) */}
          {!selectedTopicId && (activeCategory || activeLevel || searchTerm) && (
            <div className="max-w-4xl mx-auto p-8 lg:p-16 animate-fadeIn">
              <button 
                onClick={() => { setActiveCategory(''); setActiveLevel(''); setSearchTerm(''); }}
                className="flex items-center gap-2 text-sm font-bold text-brand-text/50 hover:text-brand-text mb-8 transition-colors group"
              >
                <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Kthehu te Kategoritë
              </button>
              
              <div className="mb-10">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-primary mb-2 block">Rezultatet</span>
                <h2 className="text-3xl font-black text-brand-text">
                  {searchTerm ? `Rezultatet e kërkimit: "${searchTerm}"` : 
                   activeCategory ? GRAMMAR_CATEGORIES.find(c => c.id === activeCategory)?.title : 
                   `Niveli ${activeLevel}`}
                </h2>
                <p className="text-brand-text/60 mt-2">Zgjidhni një leksion më poshtë për të filluar.</p>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {filteredTopics.map((topic, idx) => (
                  <button
                    key={topic.id}
                    onClick={() => handleTopicSelect(topic.id)}
                    className="flex items-center justify-between p-5 bg-white rounded-2xl shadow-sm hover:shadow-lg hover:shadow-brand-accent/10 transition-all text-left group animate-fadeIn"
                    style={{ animationDelay: `${idx * 0.03}s` }}
                  >
                    <div className="flex items-center gap-4">
                      <LevelBadge level={topic.level} />
                      <div>
                        <h4 className="font-bold text-brand-text group-hover:text-brand-primary transition-colors">{topic.title}</h4>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] font-medium text-brand-text/50 uppercase tracking-wider">
                            {GRAMMAR_CATEGORIES.find(c => c.id === topic.category)?.title}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-brand-primary transition-colors">
                      <ChevronRightIcon className="w-4 h-4 text-brand-text/50 group-hover:text-brand-text" />
                    </div>
                  </button>
                ))}
                {filteredTopics.length === 0 && (
                  <div className="py-20 text-center">
                    <XCircleIcon className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                    <p className="text-brand-text/50">Nuk u gjet asnjë leksion.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STATE 3: Selected Topic Content */}
          {selectedTopic && !quizActive && (
            <div className="max-w-3xl mx-auto p-6 md:p-12 lg:p-16 animate-fadeIn min-h-screen">
              {/* Back to Results / List */}
              <button 
                onClick={() => setSelectedTopicId(null)}
                className="flex items-center gap-2 text-sm font-bold text-brand-text/50 hover:text-brand-text mb-8 transition-colors group"
              >
                <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Kthehu te Lista
              </button>

              {/* Header */}
              <div className="mb-12 border-b border-brand-secondary/15 pb-8 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-bold text-brand-primary uppercase tracking-widest">
                      {GRAMMAR_CATEGORIES.find(c => c.id === selectedTopic.category)?.title}
                    </span>
                    {selectedTopic.level && <LevelBadge level={selectedTopic.level} />}
                  </div>
                  <h2 className="text-2xl font-bold text-brand-text mb-4">{selectedTopic.title}</h2>

                  <div className="p-1 px-3 bg-gray-100 inline-flex items-center gap-2 rounded-md text-xs font-semibold text-brand-text/70">
                    <HeroIcons.ClockIcon className="w-3.5 h-3.5" />
                    15 minuta lexim
                  </div>
                </div>

                <button
                  onClick={() => toggleBookmark(selectedTopic)}
                  disabled={bookmarkLoading}
                  title={bookmarkedIds.has(selectedTopic.id) ? 'Hiq nga të ruajturat' : 'Ruaj temën'}
                  aria-label={bookmarkedIds.has(selectedTopic.id) ? 'Hiq nga të ruajturat' : 'Ruaj temën'}
                  className="p-2 rounded-full border border-zinc-200 hover:bg-zinc-50 transition-colors cursor-pointer shrink-0 text-zinc-500 hover:text-zinc-800"
                >
                  {bookmarkedIds.has(selectedTopic.id) ? (
                    <BookmarkSolidIcon className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <BookmarkOutlineIcon className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Detailed HTML Content */}
              <div
                className="grammar-prose max-w-none text-brand-text/80 mb-8"
                dangerouslySetInnerHTML={{ __html: selectedTopic.detailedContent }}
              />

              {/* Tables */}
              {selectedTopic.tables && selectedTopic.tables.map((table, tIndex) => (
                <div key={tIndex} className="mb-12 group animate-fadeIn" style={{ animationDelay: `${tIndex * 0.1}s` }}>
                  <h4 className="text-sm font-bold text-brand-text/60 uppercase tracking-wider mb-6 flex items-center gap-3">
                    <div className="h-[2px] w-8 bg-brand-primary rounded-full"></div>
                    {table.label}
                  </h4>
                  <div className="overflow-hidden rounded-3xl border border-brand-secondary/20 shadow-[0_20px_50px_rgba(0,0,0,0.05)] bg-white">
                    <table className="w-full text-left border-separate border-spacing-0">
                      <thead>
                        <tr className="bg-brand-primary">
                          {table.headers.map((h, i) => (
                            <th key={i} className="p-5 text-[10px] font-bold text-white/80 uppercase tracking-widest border-r border-white/20 last:border-0">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {table.rows.map((row, rIndex) => (
                          <tr key={rIndex} className="group/row transition-all hover:bg-brand-primary/10">
                            {row.map((cell, cIndex) => (
                              <td key={cIndex} className={`p-5 text-sm transition-colors border-b border-brand-secondary/15 ${cIndex === 0
                                  ? 'font-bold text-brand-text bg-gray-50/40 w-1/3'
                                  : 'text-brand-text/70 group-hover/row:text-brand-text'
                                } ${rIndex === table.rows.length - 1 ? 'border-b-0' : ''}`}>
                                <div className="flex items-center gap-2">
                                  {cIndex === 0 && <div className="w-1.5 h-1.5 rounded-full bg-brand-primary opacity-0 group-hover/row:opacity-100 transition-opacity"></div>}
                                  {cell}
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {/* Examples */}
              {selectedTopic.examples && selectedTopic.examples.length > 0 && (
                <div className="mb-16 bg-brand-primary/10 border border-brand-primary/15 rounded-2xl p-8">
                  <h4 className="text-lg font-bold text-brand-text mb-4 flex items-center gap-2">
                    <HeroIcons.LanguageIcon className="w-6 h-6" />
                    Shembuj nga Përdorimi
                  </h4>
                  <div className="space-y-4">
                    {selectedTopic.examples.map((ex, i) => (
                      <Card key={i}>
                        <CardContent className="p-5 flex items-center justify-between gap-4">
                          <div>
                            <p className="text-sm font-bold text-brand-text mb-1">{ex.ger}</p>
                            <p className="text-sm text-brand-primary font-medium italic">/ {ex.alb}</p>
                          </div>
                          <SpeakButton text={ex.ger} lang="de-DE" />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Quiz Call to Action */}
              <Card className="text-center p-12 bg-zinc-950 text-white border-zinc-800 shadow-2xl overflow-hidden relative">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-brand-accent/20 to-transparent" />
                <div className="relative">
                  <TrophyIcon className="w-16 h-16 text-brand-accent mx-auto mb-6" />
                  <h3 className="text-2xl font-bold mb-4">Gati për të matur dijet?</h3>
                  <p className="text-brand-text/50 max-w-md mx-auto mb-8">Plotëso ushtrime rreth kësaj teme për të përforcuar njohuritë e tua të reja.</p>
                  <Button
                    onClick={startQuiz}
                    disabled={!selectedTopic.exercises || selectedTopic.exercises.length === 0}
                    size="lg"
                    className="px-10 shadow-lg hover:shadow-brand-accent/20 active:scale-95"
                  >
                    Fillo Ushtrimet ({selectedTopic.exercises?.length || 0} pyetje)
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {/* Quiz UI */}
          {quizActive && (
            <div className="max-w-2xl mx-auto p-6 md:p-12 lg:p-16 h-full flex flex-col justify-center animate-fadeIn">
              {!showResult ? (
                <div>
                  <div className="flex items-center justify-between mb-8">
                    <button onClick={resetQuiz} className="text-brand-text/50 hover:text-brand-text/70 flex items-center gap-2 text-sm font-medium">
                      <ArrowLeftIcon className="w-4 h-4" /> Anulo
                    </button>
                    <div className="text-sm font-bold text-brand-primary">
                      Pyetja {currentQuestionIndex + 1} nga {selectedTopic.exercises.length}
                    </div>
                  </div>

                  <div className="w-full bg-gray-100 h-1.5 rounded-full mb-12 overflow-hidden">
                    <div
                      className="bg-brand-primary h-full transition-all duration-500"
                      style={{ width: `${((currentQuestionIndex + 1) / selectedTopic.exercises.length) * 100}%` }}
                    />
                  </div>

                  <h3 className="text-2xl font-bold text-brand-text mb-10 leading-tight">
                    {selectedTopic.exercises[currentQuestionIndex].question}
                  </h3>

                  <div className="space-y-3">
                    {selectedTopic.exercises[currentQuestionIndex].options.map((option, idx) => (
                      <button
                        key={idx}
                        disabled={!!answerFeedback}
                        onClick={() => handleAnswerClick(idx)}
                        className={`w-full p-5 rounded-xl text-left font-bold transition-all border-2 flex items-center justify-between group ${answerFeedback
                            ? idx === selectedTopic.exercises[currentQuestionIndex].correct
                              ? 'bg-green-50 border-green-500 text-green-700'
                              : idx === userAnswers[currentQuestionIndex]?.selectedIndex
                                ? 'bg-red-50 border-red-500 text-red-700'
                                : 'bg-white border-brand-secondary/15 opacity-50'
                            : 'bg-white border-brand-secondary/20 hover:border-brand-primary/40 hover:bg-brand-primary/10/30'
                          }`}
                      >
                        <span>{option}</span>
                        {answerFeedback && idx === selectedTopic.exercises[currentQuestionIndex].correct && (
                          <CheckCircleIcon className="w-6 h-6 text-green-500" />
                        )}
                        {answerFeedback && idx === userAnswers[currentQuestionIndex]?.selectedIndex && idx !== selectedTopic.exercises[currentQuestionIndex].correct && (
                          <XCircleIcon className="w-6 h-6 text-red-500" />
                        )}
                      </button>
                    ))}
                  </div>

                  {answerFeedback && (
                    <div className="mt-10 p-6 bg-gray-50 rounded-2xl animate-fadeIn border border-brand-secondary/20">
                      <p className="text-sm text-brand-text/70 leading-relaxed italic mb-6">
                        {answerFeedback.explanation}
                      </p>
                      <Button
                        onClick={nextQuestion}
                        className="w-full py-4 h-auto font-bold"
                      >
                        Vazhdo te pyetja tjetër <ArrowRightIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className={`w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-8 ${score > 7 ? 'bg-green-100 text-green-600' : 'bg-brand-primary/15 text-brand-primary'}`}>
                    <TrophyIcon className="w-16 h-16" />
                  </div>
                  <h2 className="text-4xl font-black text-brand-text mb-4">Ju përfunduat!</h2>
                  <p className="text-xl text-brand-text/70 mb-10">Rezultati juaj: <span className="font-bold text-brand-primary">{score} nga {selectedTopic.exercises.length}</span> të sakta.</p>
                  <div className="flex gap-4 max-w-md mx-auto">
                    <button
                      onClick={resetQuiz}
                      className="flex-1 py-4 bg-brand-primary text-brand-text font-bold rounded-xl hover:bg-brand-primary"
                    >
                      Kthehu te leksioni
                    </button>
                    <button
                      onClick={startQuiz}
                      className="flex-1 py-4 bg-gray-100 text-brand-text/70 font-bold rounded-xl hover:bg-brand-primary/10"
                    >
                      Provo përsëri
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ComprehensiveGrammarPage;
